import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const NotificacionesContext = createContext();

export const useNotificaciones = () => {
  const context = useContext(NotificacionesContext);
  if (!context) {
    throw new Error('useNotificaciones debe usarse dentro de NotificacionesProvider');
  }
  return context;
};

export const NotificacionesProvider = ({ children }) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [resumen, setResumen] = useState({
    total: 0, criticas: 0, altas: 0, medias: 0,
    bajas: 0, preventivas: 0, conDemoras: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);

  // Intervalo de polling (ms) — se actualiza con la config de la BD
  const [pollIntervalMs, setPollIntervalMs] = useState(60000);

  // Config de push — usamos ref para no recrear fetchNotificaciones en cada cambio de config
  const pushConfigRef = useRef({
    push_activo: true,
    push_sonido: true,
    notif_alerta_alta: true,
    notif_critica: true,
    notif_nuevo: false,
    notif_recalculo: false,
  });

  // IDs ya vistos para detectar notificaciones nuevas
  const idsVistosRef = useRef(null); // null = primera carga, no disparar "nuevo"

  // ── Cargar config pública y mostrar resumen al login ─────────────────────
  const cargarConfigYAlertar = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await fetch(`${API_URL}/configuracion/publica`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) return;
      const config = await res.json();

      pushConfigRef.current = {
        push_activo:       config.push_activo       ?? true,
        push_sonido:       config.push_sonido       ?? true,
        notif_alerta_alta: config.notif_alerta_alta ?? true,
        notif_critica:     config.notif_critica     ?? true,
        notif_nuevo:       config.notif_nuevo       ?? false,
        notif_recalculo:   config.notif_recalculo   ?? false,
      };

      const intervaloMs = Math.max(10, config.push_notificaciones_intervalo || 60) * 1000;
      setPollIntervalMs(intervaloMs);

      // Mostrar resumen de alertas activas al iniciar sesión
      if (!pushConfigRef.current.push_activo) return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;

      const resNotifs = await fetch(`${API_URL}/notificaciones/activas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dataN = await resNotifs.json();
      if (!dataN.success) return;

      const notifs = dataN.notificaciones || [];
      const criticas = notifs.filter(n =>
        n.urgencia === 'critica' || n.tipo === 'ULTIMO_DIA_SIN_DEMORAS'
      );
      const altas = notifs.filter(n => n.urgencia === 'alta');

      if (pushConfigRef.current.notif_critica && criticas.length > 0) {
        const ultimoDia = criticas.filter(n => n.tipo === 'ULTIMO_DIA_SIN_DEMORAS').length;
        new Notification(
          ultimoDia > 0
            ? `🚨 ${ultimoDia} contenedor(es) vencen HOY`
            : `⚠️ ${criticas.length} alerta(s) crítica(s) activas`,
          {
            body: 'Revisar el panel de notificaciones',
            icon: '/images/apple-touch-icon.png',
            tag: 'login-summary',
            requireInteraction: true,
            silent: !pushConfigRef.current.push_sonido,
          }
        );
      } else if (pushConfigRef.current.notif_alerta_alta && altas.length > 0) {
        new Notification(`🟠 ${altas.length} contenedor(es) en alerta alta`, {
          body: 'Próximos a generar cargos por demora',
          icon: '/images/apple-touch-icon.png',
          tag: 'login-summary',
          silent: !pushConfigRef.current.push_sonido,
        });
      }

    } catch (_) {
      // Silenciar — usar defaults del ref
    }
  }, []);

  useEffect(() => {
    // Intentar cargar al montar (si ya hay sesión activa al refrescar página)
    cargarConfigYAlertar();

    // Recargar config y alertar cada vez que el usuario inicia sesión
    window.addEventListener('user-login', cargarConfigYAlertar);
    return () => window.removeEventListener('user-login', cargarConfigYAlertar);
  }, [cargarConfigYAlertar]);

  // ── Mostrar notificación del navegador ───────────────────────────────────
  const mostrarNotificacionNavegador = useCallback((titulo, body, requireInteraction = false) => {
    if (!pushConfigRef.current.push_activo) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    new Notification(titulo, {
      body,
      icon: '/images/apple-touch-icon.png',
      tag: 'demoras-notification',
      requireInteraction,
      silent: !pushConfigRef.current.push_sonido,
    });
  }, []);

  // ── Fetch notificaciones ─────────────────────────────────────────────────
  const fetchNotificaciones = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/notificaciones/activas`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (!data.success) throw new Error(data.error || 'Error al obtener notificaciones');

      const notifs = data.notificaciones || [];
      setNotificaciones(notifs);

      const preventivas = notifs.filter(n =>
        n.tipo === 'ALERTA_PREVENTIVA' || n.tipo === 'ULTIMO_DIA_SIN_DEMORAS'
      ).length;
      const conDemoras = notifs.filter(n => n.tipo === 'DEMORAS_ACTIVAS').length;

      setResumen({
        total: data.resumen?.total || notifs.length,
        criticas: data.resumen?.criticas || notifs.filter(n => n.urgencia === 'critica').length,
        altas: data.resumen?.altas || notifs.filter(n => n.urgencia === 'alta').length,
        medias: data.resumen?.medias || notifs.filter(n => n.urgencia === 'media').length,
        bajas: data.resumen?.bajas || notifs.filter(n => n.urgencia === 'baja').length,
        preventivas,
        conDemoras
      });

      setUltimaActualizacion(new Date());

      // ── Notificaciones del navegador ──────────────────────────────────
      const cfg = pushConfigRef.current;
      const esFirstLoad = idsVistosRef.current === null;

      if (!esFirstLoad) {
        const idsActuales = new Set(notifs.map(n => n.id));

        // notif_nuevo: hay IDs que no habíamos visto antes
        if (cfg.notif_nuevo) {
          const nuevas = notifs.filter(n => !idsVistosRef.current.has(n.id));
          if (nuevas.length > 0) {
            mostrarNotificacionNavegador(
              `🔔 ${nuevas.length} nueva(s) alerta(s)`,
              nuevas.map(n => n.titulo || n.contenedor).join(', ')
            );
          }
        }

        // notif_critica: urgencia crítica o último día sin demoras
        if (cfg.notif_critica) {
          const criticas = notifs.filter(n =>
            (n.urgencia === 'critica' || n.tipo === 'ULTIMO_DIA_SIN_DEMORAS') &&
            !idsVistosRef.current.has(n.id)
          );
          if (criticas.length > 0) {
            const ultimoDia = criticas.filter(n => n.tipo === 'ULTIMO_DIA_SIN_DEMORAS').length;
            if (ultimoDia > 0) {
              mostrarNotificacionNavegador(
                `🚨 ${ultimoDia} contenedor(es) vencen HOY`,
                'Requieren acción inmediata para evitar demoras',
                true
              );
            } else {
              mostrarNotificacionNavegador(
                `⚠️ ${criticas.length} alerta(s) crítica(s)`,
                'Contenedores con demoras activas requieren atención',
                true
              );
            }
          }
        }

        // notif_alerta_alta: urgencia alta (naranja, próximos a vencer)
        if (cfg.notif_alerta_alta && !cfg.notif_critica) {
          // Solo si no se mostró ya por critica (evitar doble popup)
          const altas = notifs.filter(n =>
            n.urgencia === 'alta' &&
            !idsVistosRef.current.has(n.id)
          );
          if (altas.length > 0) {
            mostrarNotificacionNavegador(
              `🟠 ${altas.length} contenedor(es) próximos a vencer`,
              'Revisar fechas de retiro antes de que generen demoras'
            );
          }
        } else if (cfg.notif_alerta_alta && cfg.notif_critica) {
          // Mostrar altas solo si no hay críticas nuevas (evitar spam)
          const criticasNuevas = notifs.filter(n =>
            (n.urgencia === 'critica' || n.tipo === 'ULTIMO_DIA_SIN_DEMORAS') &&
            !idsVistosRef.current.has(n.id)
          ).length;
          if (criticasNuevas === 0) {
            const altas = notifs.filter(n =>
              n.urgencia === 'alta' &&
              !idsVistosRef.current.has(n.id)
            );
            if (altas.length > 0) {
              mostrarNotificacionNavegador(
                `🟠 ${altas.length} contenedor(es) en alerta alta`,
                'Próximos a generar cargos por demora'
              );
            }
          }
        }

        // Actualizar IDs vistos
        idsVistosRef.current = new Set(notifs.map(n => n.id));
      } else {
        // Primera carga: registrar IDs sin disparar notificaciones
        idsVistosRef.current = new Set(notifs.map(n => n.id));
      }

    } catch (err) {
      console.error('Error fetching notificaciones:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mostrarNotificacionNavegador]);

  // ── Polling automático — intervalo desde la BD ───────────────────────────
  useEffect(() => {
    fetchNotificaciones();
    const interval = setInterval(fetchNotificaciones, pollIntervalMs);
    return () => clearInterval(interval);
  }, [fetchNotificaciones, pollIntervalMs]);

  // ── Recargar config cuando Ajustes guarda cambios ────────────────────────
  useEffect(() => {
    const handleConfigUpdated = (e) => {
      const detail = e.detail || {};

      if (detail.push_notificaciones_intervalo !== undefined) {
        const ms = Math.max(10, detail.push_notificaciones_intervalo) * 1000;
        setPollIntervalMs(ms);
      }

      // Actualizar el ref de push sin re-render innecesario
      pushConfigRef.current = {
        push_activo:        detail.push_activo        ?? pushConfigRef.current.push_activo,
        push_sonido:        detail.push_sonido        ?? pushConfigRef.current.push_sonido,
        notif_alerta_alta:  detail.notif_alerta_alta  ?? pushConfigRef.current.notif_alerta_alta,
        notif_critica:      detail.notif_critica      ?? pushConfigRef.current.notif_critica,
        notif_nuevo:        detail.notif_nuevo        ?? pushConfigRef.current.notif_nuevo,
        notif_recalculo:    detail.notif_recalculo    ?? pushConfigRef.current.notif_recalculo,
      };
    };

    window.addEventListener('config-updated', handleConfigUpdated);
    return () => window.removeEventListener('config-updated', handleConfigUpdated);
  }, []);

  // ── Solicitar permisos al montar ─────────────────────────────────────────
  const solicitarPermisosPush = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  };

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const timeout = setTimeout(() => solicitarPermisosPush(), 5000);
      return () => clearTimeout(timeout);
    }
  }, []);

  // ── Acciones ─────────────────────────────────────────────────────────────
  const marcarComoLeida = async (id) => {
    try {
      const response = await fetch(`${API_URL}/notificaciones/${id}/leer`, {
        method: 'PUT', headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotificaciones(prev => prev.filter(n => n.id !== id));
        setResumen(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error marcando como leida:', err);
      return false;
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      const response = await fetch(`${API_URL}/notificaciones/leer-todas`, {
        method: 'PUT', headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotificaciones([]);
        setResumen({ total: 0, criticas: 0, altas: 0, medias: 0, bajas: 0, preventivas: 0, conDemoras: 0 });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error marcando todas como leidas:', err);
      return false;
    }
  };

  const descartarNotificacion = async (id) => {
    try {
      const response = await fetch(`${API_URL}/notificaciones/${id}/descartar`, {
        method: 'PUT', headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotificaciones(prev => prev.filter(n => n.id !== id));
        setResumen(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error descartando notificacion:', err);
      return false;
    }
  };

  const forzarRecalculo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/notificaciones/recalcular`, {
        method: 'POST', headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success) {
        await fetchNotificaciones();

        // notif_recalculo: push cuando se ejecuta un recálculo manual
        if (pushConfigRef.current.notif_recalculo) {
          mostrarNotificacionNavegador(
            '🔄 Recálculo completado',
            'Los datos de demoras y costos han sido actualizados'
          );
        }

        return { success: true, message: data.message };
      }
      return { success: false, message: data.error };
    } catch (err) {
      console.error('Error en recalculo:', err);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    notificaciones,
    resumen,
    loading,
    error,
    ultimaActualizacion,
    refrescar: fetchNotificaciones,
    fetchNotificaciones,
    marcarComoLeida,
    marcarTodasComoLeidas,
    descartarNotificacion,
    forzarRecalculo,
    solicitarPermisosPush,
    permisoPush: typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  };

  return (
    <NotificacionesContext.Provider value={value}>
      {children}
    </NotificacionesContext.Provider>
  );
};

export default NotificacionesContext;
